import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { Amplify } from 'aws-amplify';
import { signOut, signIn, getCurrentUser, signInWithRedirect, fetchAuthSession } from 'aws-amplify/auth';

interface AuthUser {
  username: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  user: AuthUser | null;
  signIn: () => Promise<any>; 
  signOut: () => Promise<any>;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isAuthenticating: true,
  user: null,
  signIn: async () => {},
  signOut: async () => {},
  error: null,
  clearError: () => {},
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

//eslint-disable-next-line
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAmplifyConfigured, setIsAmplifyConfigured] = useState(false);

  const clearError = () => setError(null);

  const USER_POOL_ID = 'us-east-1_hZedfnm0A'
  const USER_POOL_CLIENT_ID = '255kupl7o7ck4lrt2h4g9aj4vi'
  const COGNITODOMAIN = 'processing-monitor-c8400057'
  const REGION ='us-east-1';

  // Configure Amplify once when the component mounts
  useEffect(() => {
    const configureAmplify = () => {
      console.log('Configuring Amplify...');

      const isLocalhost = window.location.hostname === 'localhost';
    
      try {
        Amplify.configure({
          Auth: {
            Cognito: {
              userPoolId: USER_POOL_ID,
              userPoolClientId: USER_POOL_CLIENT_ID,
              loginWith: {
                  oauth: {
                      domain: `${COGNITODOMAIN}.auth.${REGION}.amazoncognito.com`,
                      scopes: ['email', 'profile', 'openid'],
                      redirectSignIn: isLocalhost ? ['http://localhost:8090/'] : [window.location.origin],
                      redirectSignOut: isLocalhost ? ['http://localhost:8090/'] : [window.location.origin],
                      responseType: 'code',
                    },
              }
            }
          },
        });
      setIsAmplifyConfigured(true);
    } catch (error) {
      console.error('Error configuring Amplify:', error);
      setError('Failed to configure authentication. Please try refreshing the page.');
      setIsAuthenticating(false);
    }
    };

    configureAmplify();
  }, []);

  // Check auth state once Amplify is configured
  useEffect(() => {
    if (!isAmplifyConfigured) return;

    const checkAuthState = async () => {
      try {
        console.log('Checking auth state...');
        // First try to get a valid session
        try {
          const session = await fetchAuthSession();
          if (session.tokens?.accessToken) {
            const username = session.tokens.accessToken.payload.username?.toString();
            if (username) {
              console.log('Valid session found');
              setIsAuthenticated(true);
              setUser({ username });
              setIsAuthenticating(false);
              return;
            }
          }
        } catch (sessionError) {
          console.log('No valid session found', sessionError);
        }
        
        // If no valid session, try to get current user
        try {
          const currentUser = await getCurrentUser();
          console.log('Current user found:', currentUser.username);
          setIsAuthenticated(true);
          setUser({ username: currentUser.username });
        } catch (userError) {
          console.log('No current user found');
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error('Error checking auth state:', error);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsAuthenticating(false);
      }
    };

    checkAuthState();
  }, [isAmplifyConfigured]);

  const doSignInWithRedirect = async () => {
    if (!isAmplifyConfigured) {
      setError('Authentication not configured yet. Please wait or refresh the page.');
      return;
    }

    setError(null);
    try {
      setIsAuthenticating(true);
      
      // First check if we already have a valid session
      try {
        const session = await fetchAuthSession();
        if (session.tokens?.accessToken) {
          const username = session.tokens.accessToken.payload.username?.toString();
          if (username) {
            console.log('Valid session exists, no need to redirect');
            setIsAuthenticated(true);
            setUser({ username });
            setIsAuthenticating(false);
            return session;
          }
        }
      } catch (sessionError) {
        console.log('No existing session, proceeding with redirect', sessionError);
      }
      
      // If no valid session, initiate the redirect flow
      console.log('Initiating sign in with redirect...');
      await signInWithRedirect({
        provider: { custom: 'trane-residential-cognito' },
      });
      
      // Note: The code below will only execute if the redirect doesn't happen
      // or if we return from the redirect with a token already
      console.log('Returned from redirect, checking user...');
      const user = await getCurrentUser();
      setIsAuthenticated(true);
      setUser({ username: user.username });
      return user;
    } catch (error) {
      console.error('Sign in error:', error);
      if (error instanceof Error) {
        setError(`Authentication error: ${error.message}`);
      } else {
        setError('Failed to sign in. Please try again.');
      }
      throw error;
    } finally {
      setIsAuthenticating(false);
    }
  };

  const doSignIn = async (username: string, password: string) => {
    setError(null);
    try {
      setIsAuthenticating(true);
      const response = await signIn({username, password});
      console.log('Sign in response:', response);
    //   if (!isSignedIn) {
    //     throw new Error('Failed to sign in');
    //     }
        const user = await getCurrentUser();
        console.log('User:', user);
      setIsAuthenticated(true);
      setUser({
        username: user.username,
        // email: user.attributes?.email || '',
        // attributes: user.attributes || {},
      });
      return user;
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Failed to sign in');
      }
      throw error;
    } finally {
      setIsAuthenticating(false);
    }
  };

  const doSignOut = async () => {
    if (!isAmplifyConfigured) {
      setError('Authentication not configured yet. Please wait or refresh the page.');
      return;
    }

    try {
      console.log('Signing out...');
      await signOut({ global: true });
      setIsAuthenticated(false);
      setUser(null);
      console.log('Sign out complete');
    } catch (error) {
      console.error('Sign out error:', error);
      if (error instanceof Error) {
        setError(`Sign out error: ${error.message}`);
      } else {
        setError('Failed to sign out. Please try again.');
      }
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isAuthenticating,
        user,
        signIn: doSignIn,
        signOut: doSignOut,
        error,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};